import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { User } from '@/types/user';
import * as usersApi from '../endpoints/users';
import { PaginatedUserResponse, SearchUsersParams } from '../endpoints/users';

export const PROFILE_QUERY_KEYS = {
  all: ['profile'] as const,
  user: (id: string) => [...PROFILE_QUERY_KEYS.all, 'user', id] as const,
  currentUser: () => [...PROFILE_QUERY_KEYS.all, 'current'] as const,
} as const;

// Re-export ProfileData type for backward compatibility
export interface ProfileData {
  user: User;
}

/**
 * Domain-oriented query options for user operations
 * Provides reusable queryOptions that can be used in hooks or prefetching
 */

/**
 * Query options for current user profile
 * Requires userId parameter for the API call (backend requires ID in path)
 */
export const currentUserQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: PROFILE_QUERY_KEYS.currentUser(), // Query key doesn't include userId for consistency
    queryFn: async (): Promise<ProfileData> => {
      return usersApi.getCurrentUser(userId);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    meta: { persist: true },
  });

/**
 * Query options for user profile by ID
 */
export const userQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: PROFILE_QUERY_KEYS.user(userId),
    queryFn: async (): Promise<ProfileData> => {
      return usersApi.getUserById(userId);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

/**
 * Query options for user profile by username
 */
export const userByUsernameQueryOptions = (username: string) =>
  queryOptions({
    queryKey: [...PROFILE_QUERY_KEYS.all, 'username', username.toLowerCase().replace(/^@+/, '')] as const,
    queryFn: async (): Promise<ProfileData> => {
      return usersApi.getUserByUsername(username);
    },
    enabled: !!username,
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

/**
 * Query keys for user search
 */
export const USERS_SEARCH_QUERY_KEYS = {
  all: ['users', 'search'] as const,
  search: (params: SearchUsersParams) => [...USERS_SEARCH_QUERY_KEYS.all, params] as const,
} as const;

/**
 * Query options for searching users with text query
 *
 * @param params - Search parameters including query text and pagination
 * @returns Query options for the users search
 *
 * @example
 * ```typescript
 * // Search for users matching "john"
 * const options = usersSearchQueryOptions({
 *   query: 'john',
 *   per_page: 20
 * });
 * ```
 */
export const usersSearchQueryOptions = (params: SearchUsersParams = {}) =>
  queryOptions({
    queryKey: USERS_SEARCH_QUERY_KEYS.search(params),
    queryFn: async (): Promise<PaginatedUserResponse> => {
      return usersApi.searchUsers(params);
    },
    enabled: (params.query?.trim().length ?? 0) >= 2, // Only search if query is at least 2 characters
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

export const usersSearchInfiniteQueryOptions = (params: Omit<SearchUsersParams, 'page'>) =>
  infiniteQueryOptions({
    queryKey: [...USERS_SEARCH_QUERY_KEYS.all, params, 'infinite'] as const,
    queryFn: async ({ pageParam }): Promise<PaginatedUserResponse> => {
      return usersApi.searchUsers({ ...params, page: pageParam as number });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.total_pages ? lastPage.page + 1 : undefined,
    enabled: (params.query?.trim().length ?? 0) >= 2,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 15,
    meta: { persist: false },
  });
